/**
 * The bulk promotion vocabulary, and the sets derived from it. These names are persisted and matched
 * by name, so each enum mirrors the Java enum named on it; a value that differs there never matches
 * an incoming row.
 */

/** Per-component outcome in an executed batch. The SKIPPED_ values are not failures. */
export enum BulkPromoteDisposition {
    INITIATED = 'INITIATED',
    BLOCKED_AT_PREFLIGHT = 'BLOCKED_AT_PREFLIGHT',
    BLOCKED_AT_REVALIDATION = 'BLOCKED_AT_REVALIDATION',
    SKIPPED_UNCHANGED = 'SKIPPED_UNCHANGED',
    SKIPPED_TARGET_AWAITING_APPROVAL = 'SKIPPED_TARGET_AWAITING_APPROVAL',
    BARRIER_FAILED = 'BARRIER_FAILED',
    FAILED_TO_INITIATE = 'FAILED_TO_INITIATE',
}

/** How far preflight has got, as reported on a progress frame. */
export enum BulkPromotePhase {
    RESOLVING = 'RESOLVING',
    VALIDATING = 'VALIDATING',
    BLOCKED_LIST_READY = 'BLOCKED_LIST_READY',
    EXECUTING_WAVE = 'EXECUTING_WAVE',
    AT_BARRIER = 'AT_BARRIER',
    COMPLETE = 'COMPLETE',
}

/**
 * Why a component is not promotable. Mirrors BulkBlockedReason.java in ICTransferPricingScheduledJobs,
 * matched BY NAME: BulkEvaluateComponent and BulkPromoteItemComponent persist `reason.name()`, and the
 * mapper passes it through untouched onto BlockedItem.reason.
 *
 * These are the service's seven values and nothing else. The previous list held fifteen client-invented
 * names, of which only three ever matched — so twelve authored remedies were unreachable, and four were
 * near-misses of reasons that DO arrive (SOURCE_NOT_ACTIVE for SOURCE_NOT_PROMOTABLE, TYPE_DISABLED for
 * PROMOTION_DISABLED, PROMOTION_REFUSED for REFUSED_AT_PROMOTION, DEPENDENCY_PARITY_INDETERMINATE for
 * VALIDATION_INDETERMINATE). A name that differs from the Java enum's silently never matches an incoming
 * row, so adding one here is only correct if it is added there too.
 */
export enum BulkBlockedReason {
    DEPENDENCY_NOT_IN_TARGET = 'DEPENDENCY_NOT_IN_TARGET',
    SOURCE_NOT_PROMOTABLE = 'SOURCE_NOT_PROMOTABLE',
    UNCHANGED_SINCE_LAST_PROMOTION = 'UNCHANGED_SINCE_LAST_PROMOTION',
    IN_FLIGHT_CONFLICT = 'IN_FLIGHT_CONFLICT',
    PROMOTION_DISABLED = 'PROMOTION_DISABLED',
    REFUSED_AT_PROMOTION = 'REFUSED_AT_PROMOTION',
    /** Kept: recorded on batches raised before the preflight chunk learned to retry an undetermined read. */
    VALIDATION_INDETERMINATE = 'VALIDATION_INDETERMINATE',
}

/** The one reason that is not a failure: already identical in the target. Named so it can be asserted. */
export const UNCHANGED_BANDS_AS_SUCCESS = BulkBlockedReason.UNCHANGED_SINCE_LAST_PROMOTION;

/**
 * How hard the dependency check was. PARITY and EXISTENCE are the service's own values — see
 * VALIDATION_MODE_PARITY / VALIDATION_MODE_EXISTENCE in ICTransferPricingWebAPILambda Constants.java.
 * Only PARITY checked that a dependency matches the source; EXISTENCE checked only that it is present.
 */
export enum BulkValidationMode {
    /** Existence and content parity. The only mode that is a clean pass. */
    PARITY = 'PARITY',
    /** Present in the target, but not compared against the source. */
    EXISTENCE = 'EXISTENCE',
    /** Never emitted by the service. Retained so an older recorded verdict still bands. */
    DEGRADED_TARGET_READS_DISABLED = 'DEGRADED_TARGET_READS_DISABLED',
    /** Never emitted by the service. Retained so an older recorded verdict still bands. */
    DEPENDENCIES_UNVERIFIED = 'DEPENDENCIES_UNVERIFIED',
}

/** Modes in which nothing about the target was read. */
export const UNVERIFIED_VALIDATION_MODES: ReadonlySet<string> = new Set([
    BulkValidationMode.EXISTENCE,
    BulkValidationMode.DEGRADED_TARGET_READS_DISABLED,
    BulkValidationMode.DEPENDENCIES_UNVERIFIED,
]);

/** Whether the operator can act on a prerequisite themselves. */
export enum PrerequisiteActionability {
    PROMOTABLE = 'PROMOTABLE',
    AWAITING_TARGET_APPROVAL = 'AWAITING_TARGET_APPROVAL',
    NOT_SELECTABLE = 'NOT_SELECTABLE',
}

/**
 * Lifecycle of one batch. Mirrors BulkPromotionStatus.java:
 *
 *   ACCEPTED -> PREFLIGHT_RUNNING -> AWAITING_CONFIRMATION -> CONFIRMED -> RUNNING
 *            -> SUCCEEDED / PARTIALLY_SUCCEEDED / FAILED / ABANDONED
 *
 * AWAITING_CONFIRMATION is neither in flight nor finished, and is what the resume path turns on.
 */
export enum BulkPromotionStatus {
    ACCEPTED = 'ACCEPTED',
    PREFLIGHT_RUNNING = 'PREFLIGHT_RUNNING',
    AWAITING_CONFIRMATION = 'AWAITING_CONFIRMATION',
    CONFIRMED = 'CONFIRMED',
    RUNNING = 'RUNNING',
    /** Terminal. Blocked components do not make a batch unsuccessful. */
    SUCCEEDED = 'SUCCEEDED',
    /** Terminal. At least one component failed and at least one promoted. */
    PARTIALLY_SUCCEEDED = 'PARTIALLY_SUCCEEDED',
    /** Terminal. At least one component failed and none promoted. */
    FAILED = 'FAILED',
    /** Terminal. The verdict expired and the operator never confirmed it. */
    ABANDONED = 'ABANDONED',

    // Not persisted by the service. Kept so an older recorded response still bands; add no new uses.
    /** @deprecated SUCCEEDED is the persisted status. */
    COMPLETED = 'COMPLETED',
    /** @deprecated PARTIALLY_SUCCEEDED is the persisted status. */
    COMPLETED_WITH_FAILURES = 'COMPLETED_WITH_FAILURES',
    /** @deprecated never a batch status; blocked is a per-component outcome. */
    BLOCKED = 'BLOCKED',
}

/**
 * From BulkPromotionStatus.isTerminal(). The legacy spellings are recognised so a response from an
 * older service is not misread as in-flight.
 */
export const BULK_PROMOTE_TERMINAL_STATUSES: ReadonlySet<string> = new Set([
    BulkPromotionStatus.SUCCEEDED,
    BulkPromotionStatus.PARTIALLY_SUCCEEDED,
    BulkPromotionStatus.FAILED,
    BulkPromotionStatus.ABANDONED,
    BulkPromotionStatus.COMPLETED,
    BulkPromotionStatus.COMPLETED_WITH_FAILURES,
]);
